import React, { useMemo } from 'react';
import { 
  TrendingUp, 
  AlertTriangle,
  CheckCircle2,
  Target
} from 'lucide-react';

// Get completion color based on compliance percentage (Yes answers)
const getCompletionColor = (percent) => {
  if (percent === 100) return { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/50', label: 'Compliant' };
  if (percent >= 75) return { bg: 'bg-lime-500', text: 'text-lime-400', border: 'border-lime-500/50', label: 'Almost There' };
  if (percent >= 50) return { bg: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-500/50', label: 'In Progress' };
  if (percent >= 25) return { bg: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/50', label: 'Started' };
  if (percent > 0) return { bg: 'bg-red-400', text: 'text-red-400', border: 'border-red-500/50', label: 'Needs Work' };
  return { bg: 'bg-red-600', text: 'text-red-500', border: 'border-red-600/50', label: 'Not Started' };
};

// Get compliance score color
const getComplianceColor = (score) => {
  if (score >= 90) return 'text-emerald-400';
  if (score >= 70) return 'text-lime-400';
  if (score >= 50) return 'text-yellow-400';
  if (score >= 25) return 'text-orange-400';
  return 'text-red-400';
};

// Domain abbreviations
const DOMAIN_ABBREV = {
  'Access Control': 'AC',
  'Awareness and Training': 'AT',
  'Audit and Accountability': 'AU',
  'Configuration Management': 'CM',
  'Identification and Authentication': 'IA',
  'Incident Response': 'IR',
  'Maintenance': 'MA',
  'Media Protection': 'MP',
  'Personnel Security': 'PS',
  'Physical Protection': 'PE',
  'Risk Assessment': 'RA',
  'Security Assessment': 'CA',
  'System and Communications Protection': 'SC',
  'System and Information Integrity': 'SI',
  'Administrative Safeguards': 'AS',
  'Technical Safeguards': 'TS',
  'Organizational Requirements': 'OR',
  'Policies and Procedures': 'PP'
};

const DomainHeatmap = ({ 
  questions, 
  onSelectDomain,
  selectedDomain,
  compact = false 
}) => {
  // Calculate domain statistics
  const { domainStats, sortedDomains, overallStats } = useMemo(() => {
    const stats = {};
    
    questions.forEach(q => {
      const domain = q.category || q.domain || 'Uncategorized';
      
      if (!stats[domain]) {
        stats[domain] = {
          total: 0,
          answered: 0,
          yes: 0,
          no: 0,
          na: 0,
          unanswered: 0
        };
      }
      
      stats[domain].total++;
      
      const answer = q.answer || 'N';  // Default is now 'No' - must earn Yes
      if (answer === 'Y') {
        stats[domain].answered++;
        stats[domain].yes++;
      } else if (answer === 'N') {
        stats[domain].answered++;
        stats[domain].no++;
      } else if (answer === 'NA') {
        stats[domain].answered++;
        stats[domain].na++;
      } else {
        stats[domain].unanswered++;
      }
    });

    // Calculate compliance percentages (Yes / Total applicable)
    // Default answer is now 'No' - must earn 'Yes'
    Object.keys(stats).forEach(domain => {
      const s = stats[domain];
      // Compliance = Yes / (Yes + No) - excludes N/A
      const applicable = s.yes + s.no;
      s.compliancePercent = applicable > 0 ? Math.round((s.yes / applicable) * 100) : 0;
      // For display, also track overall compliance including N/A
      s.completionPercent = s.compliancePercent; // Use compliance as main metric
      s.complianceScore = s.compliancePercent;
    });

    // Sort domains by compliance (lowest first for attention priority)
    const sorted = Object.keys(stats).sort((a, b) => {
      // First by compliance percent (lowest first - needs most attention)
      if (stats[a].compliancePercent !== stats[b].compliancePercent) {
        return stats[a].compliancePercent - stats[b].compliancePercent;
      }
      // Then by total questions (more questions = higher priority)
      return stats[b].total - stats[a].total;
    });

    // Overall stats
    const totalYes = questions.filter(q => q.answer === 'Y').length;
    const totalNo = questions.filter(q => q.answer === 'N' || !q.answer).length;
    const totalNA = questions.filter(q => q.answer === 'NA').length;
    const totalApplicable = totalYes + totalNo;
    
    return {
      domainStats: stats,
      sortedDomains: sorted,
      overallStats: {
        total: questions.length,
        yes: totalYes,
        no: totalNo,
        na: totalNA,
        compliancePercent: totalApplicable > 0 ? Math.round((totalYes / totalApplicable) * 100) : 0
      }
    };
  }, [questions]);

  // Get domains that need attention (< 50% compliance)
  const needsAttention = sortedDomains.filter(d => domainStats[d].compliancePercent < 50);
  const inProgress = sortedDomains.filter(d => domainStats[d].compliancePercent >= 50 && domainStats[d].compliancePercent < 100);
  const completed = sortedDomains.filter(d => domainStats[d].compliancePercent === 100);

  const handleDomainClick = (domain) => {
    if (selectedDomain === domain) {
      onSelectDomain(null);
    } else {
      onSelectDomain(domain);
    }
  };

  return (
    <div 
      data-testid="domain-heatmap" 
      className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden"
    >
      {/* Header with Overall Progress */}
      <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-800/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-violet-400" />
            <h3 className="text-lg font-semibold text-white">Compliance Heatmap</h3>
            <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-0.5 rounded">Default: No • Earn Your Yes</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-slate-400">Compliant</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-yellow-500" />
              <span className="text-slate-400">In Progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span className="text-slate-400">Non-Compliant</span>
            </div>
          </div>
        </div>

        {/* Overall Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <div className={`text-2xl font-bold ${getComplianceColor(overallStats.compliancePercent)}`}>
              {overallStats.compliancePercent}%
            </div>
            <div className="text-xs text-slate-400">Compliance Score</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{overallStats.yes}</div>
            <div className="text-xs text-slate-400">Yes (Compliant)</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-400">{overallStats.no}</div>
            <div className="text-xs text-slate-400">No (Gaps)</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-400">{overallStats.na}</div>
            <div className="text-xs text-slate-400">N/A</div>
          </div>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="p-4">
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-2">
          {sortedDomains.map((domain) => {
            const stats = domainStats[domain];
            const colorInfo = getCompletionColor(stats.compliancePercent);
            const abbrev = DOMAIN_ABBREV[domain] || domain.substring(0, 2).toUpperCase();
            const isSelected = selectedDomain === domain;
            
            return (
              <button
                key={domain}
                data-testid={`heatmap-${domain.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleDomainClick(domain)}
                className={`relative group p-2 rounded-lg transition-all duration-200 ${
                  isSelected 
                    ? `ring-2 ring-white ring-offset-2 ring-offset-slate-800 ${colorInfo.bg}` 
                    : `${colorInfo.bg}/80 hover:${colorInfo.bg} hover:scale-105`
                }`}
                title={`${domain}: ${stats.yes}/${stats.total} compliant (${stats.compliancePercent}%)`}
              >
                {/* Domain Abbreviation */}
                <div className="text-center">
                  <div className="text-lg font-bold text-white drop-shadow-md">{abbrev}</div>
                  <div className="text-xs text-white/80 font-medium">{stats.compliancePercent}%</div>
                </div>

                {/* Hover Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 whitespace-nowrap">
                  <div className="text-sm font-semibold text-white mb-1">{domain}</div>
                  <div className="text-xs text-slate-400">
                    {stats.compliancePercent}% Compliant
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs">
                    <span className="text-green-400">✓ {stats.yes} Yes</span>
                    <span className="text-red-400">✗ {stats.no} No</span>
                    <span className="text-gray-400">— {stats.na} N/A</span>
                  </div>
                  {stats.compliancePercent < 100 && (
                    <div className="text-xs text-violet-400 mt-1">Click to address gaps</div>
                  )}
                </div>

                {/* Compliance indicator dot */}
                {stats.compliancePercent === 100 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Actions - Domains Needing Attention */}
      {needsAttention.length > 0 && (
        <div className="p-4 border-t border-slate-700 bg-red-500/5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">Compliance Gaps</span>
            <span className="text-xs text-slate-500">Click to address</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {needsAttention.map((domain) => {
              const stats = domainStats[domain];
              const abbrev = DOMAIN_ABBREV[domain] || domain.substring(0, 2).toUpperCase();
              
              return (
                <button
                  key={domain}
                  onClick={() => handleDomainClick(domain)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                    selectedDomain === domain
                      ? 'bg-red-500/30 border border-red-500/50 text-red-300'
                      : 'bg-slate-700/50 hover:bg-red-500/20 text-slate-300 hover:text-red-300'
                  }`}
                  title={domain}
                >
                  <span className="font-mono font-bold">{abbrev}</span>
                  <span className="text-xs opacity-70">{stats.no} gaps</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Success Message when all complete */}
      {completed.length === sortedDomains.length && sortedDomains.length > 0 && (
        <div className="p-4 border-t border-emerald-500/30 bg-emerald-500/10">
          <div className="flex items-center justify-center gap-2 text-emerald-400">
            <TrendingUp className="w-5 h-5" />
            <span className="font-medium">All domains complete! Review your responses before finalizing.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainHeatmap;
